/**
 * PersonModal / ProjectModal — port of designv2/src/modals.jsx.
 *
 * Differences from the prototype (PersonModal only):
 *   - sysRole is OPTIONAL on a Person (Decision 1) — the select gains a
 *     "Planning only — no login" option that maps to `null`.
 *   - Two-layer cost gating: the trigger gate at the call sites uses the
 *     demo `role`; the field gate here MUST use `authedSysRole` — see
 *     add-costs-core design.md Decision 4 for why.
 *   - Lead callers see the sysRole select as disabled (Decision 3).
 *   - Job-role suggestions are fetched from /api/job-roles, passed in via
 *     `suggestions`.
 *   - PATCH/POST for the Person is owned by the route wrapper; this modal
 *     calls `onSave(input)` for the person. Cost-rate edits are diff-saved
 *     directly via `cost-rates-api` (multiple POST/PATCH/DELETE in one
 *     `Save changes` click); `onSaveCost` is fired once afterwards so the
 *     App-level state can re-fetch the chain. The modal closes itself on
 *     success.
 *
 * ProjectModal is a verbatim port of the designv2 prototype.
 */
import { useEffect, useMemo, useState } from "react";

import { ymKeyOfDate } from "@shared/calendar";
import type {
  CostsByPerson,
  Person,
  PricingMode,
  Project,
  ProjectInput,
  ProjectStatus,
  RateHistoryEntry,
  SysRole,
  WorkloadPeriod,
} from "@shared/types";

import { DatePicker, RolePicker, TagEditor } from "./cells";
import { JOB_ROLES_SEED, roleTone } from "./data";
import { createCostRate, deleteCostRate, patchCostRate } from "./lib/costs-api";
import { sortByName } from "./lib/sort";
import { useC } from "./theme";
import { SYS_ROLES } from "./tokens";
import { Badge, Btn, Field, InfoIcon, Input, Modal, Sel } from "./ui";

export interface PersonModalSaveInput {
  name: string;
  email: string;
  sysRole: SysRole | null;
  emp: "full" | "part";
  wl: number;
  roles: string[];
  periods: WorkloadPeriod[];
  status: "active" | "archived";
  engagement?: "employee" | "contractor" | "partner";
  companyId?: number | null;
  companyName?: string | null;
  excludeFromPlanning: boolean;
}

export interface PersonModalProps {
  person: Person | null;
  /**
   * Authenticated caller's `sysRole`. Drives the sysRole-select disabled
   * state for leads AND the cost-field render gate (Decision 4 — must be
   * sourced from `authedSysRole`, not the demo `role`).
   */
  currentUserRole: SysRole;
  /** Job-role catalogue to surface as TagEditor suggestions. */
  suggestions: string[];
  /** Per-person cost map (admin/lead/accountant only — empty for PM). */
  costs: CostsByPerson;
  onClose: () => void;
  /**
   * Person PATCH (edit) or POST (create). Returns the saved Person on
   * success, undefined on caught failure (which leaves saveError populated
   * via the prop, modal stays open).
   */
  onSave: (input: PersonModalSaveInput) => Promise<Person | undefined>;
  /**
   * Fired after `onSave` succeeds so the App-level state can refresh the
   * person's cost-rate chain (which the unified editor mutates directly
   * via cost-rates-api). Absent for callers outside `[admin, lead]`. On
   * rejection the modal calls `onCostSaveFailed` and closes anyway.
   */
  onSaveCost?: (personId: number, body?: unknown) => Promise<void>;
  /** Surface a partial-failure toast at the App-level error surface. */
  onCostSaveFailed?: (personId: number, body?: unknown) => void;
  /** Optional error from the most recent save attempt — surfaced inline. */
  saveError?: string | null;
  /** Admin-only delete handler; absent in create mode. */
  onDelete?: () => void | Promise<void>;
  /**
   * Active non-time-off project list used by the per-project cost-rates
   * section dropdown. Empty list (or missing) hides the section entirely.
   */
  projects?: Array<{ id: number; name: string; status: string; isTimeOff: boolean }>;
}

const PLANNING_ONLY = "__planning__" as const;

const YM_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

const DATE_CELL_STYLE = { flex: "1 1 120px", minWidth: 0 } as const;

/** Format YYYY-MM as "Mmm YYYY" (e.g. "2026-03" → "Mar 2026"). Returns "" on
 *  empty input. */
function formatYm(ym: string): string {
  if (!YM_KEY_RE.test(ym)) return "";
  const [y, m] = ym.split("-");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const i = Number(m) - 1;
  return `${monthNames[i]} ${y}`;
}

/** Compute the contextual period label for a row given its (chronologically-
 *  sorted) siblings in the same block. Returns null when the row needs user
 *  input (a non-headline row without a start month yet) — caller falls back
 *  to the DatePicker's own placeholder. */
function periodLabel(
  row: { effectiveFrom: string },
  rowsInBlock: ReadonlyArray<{ effectiveFrom: string }>,
): string | null {
  const idx = rowsInBlock.indexOf(row);
  const isHeadline = idx === 0;
  const next = idx >= 0 ? rowsInBlock[idx + 1] : undefined;
  const nextStart = next?.effectiveFrom || "";
  const hasNext = nextStart !== "";
  const myStart = row.effectiveFrom;
  if (myStart === "") {
    if (isHeadline) {
      return hasNext ? `Until ${prevYm(nextStart)}` : "Headline (no date)";
    }
    // A new dated row the user hasn't filled yet — let the DatePicker
    // placeholder ("Pick a start month") show through.
    return null;
  }
  if (hasNext) {
    return `${formatYm(myStart)} – ${prevYm(nextStart)}`;
  }
  return `From ${formatYm(myStart)}`;
}

/** Returns the month-year label for (ymKey - 1 month), formatted "Mmm YYYY". */
function prevYm(ymKey: string): string {
  if (!YM_KEY_RE.test(ymKey)) return "";
  const [yStr, mStr] = ymKey.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const prevY = m === 1 ? y - 1 : y;
  const prevM = m === 1 ? 12 : m - 1;
  return formatYm(`${prevY}-${String(prevM).padStart(2, "0")}`);
}

/** Compute the raw YmKey for (ymKey + 1 month). Falls through to "" on bad
 *  input so callers can pre-fill with a safe-but-non-empty value. */
function nextMonthYmKey(ymKey: string): string {
  if (!YM_KEY_RE.test(ymKey)) return "";
  const [yStr, mStr] = ymKey.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const nextY = m === 12 ? y + 1 : y;
  const nextM = m === 12 ? 1 : m + 1;
  return `${nextY}-${String(nextM).padStart(2, "0")}`;
}

/** Compute the raw YmKey for (ymKey - 1 month). Returns null on empty input. */
function prevYmKey(ymKey: string): string | null {
  if (!YM_KEY_RE.test(ymKey)) return null;
  const [yStr, mStr] = ymKey.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const prevY = m === 1 ? y - 1 : y;
  const prevM = m === 1 ? 12 : m - 1;
  return `${prevY}-${String(prevM).padStart(2, "0")}`;
}

export function PersonModal({
  person,
  currentUserRole,
  suggestions,
  costs,
  projects: projectsProp,
  onClose,
  onSave,
  onSaveCost,
  onCostSaveFailed,
  saveError,
  onDelete,
}: PersonModalProps) {
  const { C } = useC();
  const isEdit = !!person;
  const isAdmin = currentUserRole === "admin";
  const isLead = currentUserRole === "lead";
  const sysRoleEditable = isAdmin || (!isLead && !isEdit);
  // Lead can create a planning-only person but cannot set sysRole at all,
  // so disable the select for leads. Admins can always edit it.

  // Cost-field gates — keyed on `currentUserRole` which is the authenticated
  // caller's sysRole (NOT the demo `role`); see Decision 4.
  const canReadCost =
    currentUserRole === "admin" || currentUserRole === "lead" || currentUserRole === "accountant";
  const canWriteCost = currentUserRole === "admin" || currentUserRole === "lead";

  const [name, setName] = useState(person?.name ?? "");
  const [email, setEmail] = useState(person?.email ?? "");
  const [status, setStatus] = useState<"active" | "archived">(person?.status ?? "active");
  const [excludeFromPlanning, setExcludeFromPlanning] = useState<boolean>(
    person?.excludeFromPlanning ?? false,
  );
  const [sysRole, setSysRole] = useState<string>(person?.sysRole ?? PLANNING_ONLY);
  const [emp, setEmp] = useState<"full" | "part">(person?.emp ?? "full");
  const [roles, setRoles] = useState<string[]>(person?.roles ?? []);
  const [busy, setBusy] = useState(false);
  const [engagement, setEngagement] = useState<"employee" | "contractor" | "partner">(
    person?.engagement ?? "employee",
  );
  const [companyId, setCompanyId] = useState<number | null>(person?.companyId ?? null);
  const [companyName, setCompanyName] = useState<string>(person?.companyName ?? "");
  const [companyError, setCompanyError] = useState<string | null>(null);
  // Tracks an explicit "Unlink" click on contractor-with-company. Submit uses
  // this to send `{companyId: null}` so the server clears the link (versus
  // omitting the key entirely, which the server interprets as "no change").
  const [companyCleared, setCompanyCleared] = useState(false);

  // ──────────────────────────────────────────────────────────────────────
  // Unified cost-rate editor state. One flat-row chain per person; the
  // editor diff-saves on submit (no per-row autosave). `loadedRows` is the
  // immutable snapshot taken on mount; `editRows` is the working set; row
  // deletions are tracked by their server id in `deletedIds`.
  //
  // A row's `localId` is monotonic-per-mount and stable across React re-
  // renders; it scopes inline error state by row regardless of whether the
  // row has been persisted yet (originalId is the *server* id; null = new).
  // ──────────────────────────────────────────────────────────────────────
  interface EditRow {
    localId: number;
    /** Server id when this row was loaded; null for in-session adds. */
    originalId: number | null;
    projectId: number | null;
    /** YYYY-MM or "" (empty = open start). */
    effectiveFrom: string;
    /** YYYY-MM or "" (empty = open end). */
    validTo: string;
    /** String for editing; parsed on submit. */
    rate: string;
    /** Snapshot at load — used by the diff to detect changes. */
    originalSnapshot: {
      projectId: number | null;
      effectiveFrom: string;
      validTo: string;
      rate: number;
    } | null;
  }
  // Narrow the dep to *this* person's chain — a costs map update for another
  // person no longer clobbers in-flight edits in this modal.
  const personRows = person ? costs[person.id] : undefined;
  const initialRows: EditRow[] = useMemo(() => {
    const loaded: EditRow[] = (personRows ?? []).map((r, i) => ({
      localId: i + 1,
      originalId: r.id,
      projectId: r.projectId,
      effectiveFrom: r.effectiveFrom ?? "",
      validTo: r.validTo ?? "",
      rate: String(r.rate),
      originalSnapshot: {
        projectId: r.projectId,
        effectiveFrom: r.effectiveFrom ?? "",
        validTo: r.validTo ?? "",
        rate: r.rate,
      },
    }));
    // The default block always has at least one row — even if the person has
    // no cost rates on file yet. Seed an empty headline draft so the editor
    // never renders an empty Default block (matches the mockup).
    if (!loaded.some((r) => r.projectId === null)) {
      loaded.unshift({
        localId: 0,
        originalId: null,
        projectId: null,
        effectiveFrom: "",
        validTo: "",
        rate: "",
        originalSnapshot: null,
      });
    }
    return loaded;
  }, [personRows]);
  const [editRows, setEditRows] = useState<EditRow[]>(initialRows);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [rowErrors, setRowErrors] = useState<Map<number, "overlap" | "invalid">>(new Map());
  const [costSaveError, setCostSaveError] = useState<string | null>(null);
  const nextLocalIdRef = useState(() => ({
    n: initialRows.length + 1,
  }))[0];

  // Re-seed editRows when the loaded chain arrives (App fetches after mount).
  useEffect(() => {
    setEditRows(initialRows);
    setDeletedIds([]);
    setRowErrors(new Map());
    nextLocalIdRef.n = initialRows.length + 1;
  }, [initialRows, nextLocalIdRef]);

  const activeProjects = useMemo(
    () => (projectsProp ?? []).filter((p) => !p.isTimeOff),
    [projectsProp],
  );
  const nonArchivedProjects = useMemo(
    () => activeProjects.filter((p) => p.status !== "archived"),
    [activeProjects],
  );
  const archivedProjects = useMemo(
    () => activeProjects.filter((p) => p.status === "archived"),
    [activeProjects],
  );
  const projectsById = useMemo(
    () => new Map(activeProjects.map((p) => [p.id, p])),
    [activeProjects],
  );

  function setRowField(
    localId: number,
    patch: Partial<Pick<EditRow, "projectId" | "effectiveFrom" | "validTo" | "rate">>,
  ) {
    setEditRows((rows) => rows.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));
    setRowErrors((m) => {
      if (!m.has(localId)) return m;
      const next = new Map(m);
      next.delete(localId);
      return next;
    });
  }
  function addRow(projectId: number | null) {
    const id = nextLocalIdRef.n++;
    setEditRows((rows) => {
      // Pre-fill effectiveFrom with a sensible future month so the new row
      // sorts AFTER the existing rows in this block. If there are dated rows
      // already, default to (latest + 1 month). Otherwise default to the
      // first month after today. A leading-headline row (empty
      // effectiveFrom) stays the chronological anchor of the block.
      const blockRows = rows.filter((r) => r.projectId === projectId);
      const latestStart = blockRows
        .map((r) => r.effectiveFrom)
        .filter((s) => s !== "")
        .sort()
        .pop();
      const todayYm = ymKeyOfDate(new Date(), "local");
      const baseline = latestStart ?? todayYm;
      const defaultStart = nextMonthYmKey(baseline);
      return [
        ...rows,
        {
          localId: id,
          originalId: null,
          projectId,
          effectiveFrom: defaultStart,
          validTo: "",
          rate: "",
          originalSnapshot: null,
        },
      ];
    });
  }
  function removeProjectGroup(projectId: number) {
    const rowsInGroup = editRows.filter((r) => r.projectId === projectId);
    if (rowsInGroup.length > 0) {
      const proj = projectsById.get(projectId);
      const name = proj?.name ?? `project #${projectId}`;
      // Confirm before dropping rows. window.confirm is fine at internal-tool
      // scale — no need for a custom dialog component.
      const ok = window.confirm(
        `Remove ${rowsInGroup.length} rate row(s) for ${name}? Saved rows will be deleted on save.`,
      );
      if (!ok) return;
    }
    // Queue deletes for persisted rows, drop everything from the editor.
    setDeletedIds((ids) => {
      const more = rowsInGroup.map((r) => r.originalId).filter((id): id is number => id !== null);
      return [...ids, ...more];
    });
    setEditRows((rows) => rows.filter((r) => r.projectId !== projectId));
    setRowErrors((m) => {
      const next = new Map(m);
      for (const r of rowsInGroup) next.delete(r.localId);
      return next;
    });
  }
  function removeRow(localId: number) {
    const row = editRows.find((r) => r.localId === localId);
    if (!row) return;
    // The default block always has at least one row — refuse to remove the
    // last one. The row stays as a draft (rate may be empty); save-time
    // validation handles the empty-rate case if the user tries to commit.
    if (row.projectId === null) {
      const defaultRowCount = editRows.filter((r) => r.projectId === null).length;
      if (defaultRowCount <= 1) return;
    }
    if (row.originalId != null) {
      setDeletedIds((ids) => [...ids, row.originalId as number]);
    }
    setEditRows((rows) => rows.filter((r) => r.localId !== localId));
    setRowErrors((m) => {
      if (!m.has(localId)) return m;
      const next = new Map(m);
      next.delete(localId);
      return next;
    });
  }

  /** Group editRows by projectId for display. NULL-project group first;
   *  remaining project groups alphabetised by Estonian collation. Within
   *  each group, NULL effectiveFrom first then YYYY-MM ASC. */
  interface GroupedRows {
    projectId: number | null;
    projectName: string;
    archived: boolean;
    rows: EditRow[];
  }
  const grouped = useMemo<GroupedRows[]>(() => {
    const byProject = new Map<number | null, EditRow[]>();
    for (const r of editRows) {
      const list = byProject.get(r.projectId) ?? [];
      list.push(r);
      byProject.set(r.projectId, list);
    }
    // Within each block, rows render in chronological order: the headline
    // (null effectiveFrom) first, then dated rows ASC. The derived "Until /
    // From / range" labels and save-time validTo computation both rely on
    // this ordering.
    const sortInBlock = (rows: EditRow[]): EditRow[] =>
      [...rows].sort((a, b) => {
        if (a.effectiveFrom === "" && b.effectiveFrom === "") return 0;
        if (a.effectiveFrom === "") return -1;
        if (b.effectiveFrom === "") return 1;
        return a.effectiveFrom.localeCompare(b.effectiveFrom);
      });
    const out: GroupedRows[] = [];
    if (byProject.has(null)) {
      out.push({
        projectId: null,
        projectName: "Default / all projects",
        archived: false,
        rows: sortInBlock(byProject.get(null) ?? []),
      });
    }
    const projectGroups: GroupedRows[] = [];
    for (const [pid, rows] of byProject) {
      if (pid === null) continue;
      const proj = projectsById.get(pid);
      projectGroups.push({
        projectId: pid,
        projectName: proj?.name ?? `Project #${pid}`,
        archived: proj?.status === "archived",
        rows: sortInBlock(rows),
      });
    }
    out.push(
      ...sortByName(projectGroups.map((g) => ({ ...g, name: g.projectName }))).map(
        ({ name: _n, ...g }) => g,
      ),
    );
    return out;
  }, [editRows, projectsById]);

  /** Project ids currently rendered as rate-row groups — exclude these
   *  from the "+ Add project rate" picker to prevent duplicate group rows
   *  appearing on the same project. */
  const usedProjectIds = useMemo(
    () => new Set(editRows.map((r) => r.projectId).filter((p): p is number => p !== null)),
    [editRows],
  );

  const initP = person?.periods.length
    ? person.periods.map((p) => ({ ...p }))
    : [{ from: "2026-01-01", to: "2026-12-31", wl: person?.wl ?? 60 }];
  const [periods, setPeriods] = useState<WorkloadPeriod[]>(initP);

  function addPeriod() {
    const last = periods[periods.length - 1];
    let newFrom = "2026-01-01";
    if (last?.to) {
      const d = new Date(last.to);
      d.setDate(d.getDate() + 1);
      newFrom = d.toISOString().slice(0, 10);
    }
    setPeriods([...periods, { from: newFrom, to: "", wl: 100 }]);
  }
  function removePeriod(idx: number) {
    setPeriods(periods.filter((_, i) => i !== idx));
  }
  function updatePeriod<K extends keyof WorkloadPeriod>(
    idx: number,
    field: K,
    val: WorkloadPeriod[K],
  ) {
    setPeriods(periods.map((p, i) => (i === idx ? { ...p, [field]: val } : p)));
  }
  function isPast(ds: string): boolean {
    if (!ds) return false;
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return new Date(ds) < t;
  }

  function checkOverlaps(): boolean {
    if (emp !== "part" || periods.length < 2) return false;
    for (let i = 0; i < periods.length; i++) {
      const a = periods[i];
      if (!a) continue;
      for (let j = i + 1; j < periods.length; j++) {
        const b = periods[j];
        if (!b) continue;
        if (!a.from || !a.to || !b.from || !b.to) continue;
        const aF = new Date(a.from).getTime();
        const aT = new Date(a.to).getTime();
        const bF = new Date(b.from).getTime();
        const bT = new Date(b.to).getTime();
        if (aF <= bT && aT >= bF) return true;
      }
    }
    return false;
  }
  const hasOverlap = checkOverlaps();

  async function submit() {
    if (busy) return;
    // Client-side mandatory check: Partner engagement requires a Company name
    // (either via existing companyId or a typed name to resolve / create).
    // Server enforces the same rule, but the field-level red + message UX is
    // smoother when caught here before the round-trip.
    setCompanyError(null);
    if (engagement === "partner" && companyId === null && companyName.trim() === "") {
      setCompanyError("Mandatory field");
      return;
    }
    setBusy(true);
    try {
      const finalPeriods = emp === "part" ? periods.filter((p) => p.from && p.to) : [];
      const lastPeriod = finalPeriods[finalPeriods.length - 1];
      const currentWl = emp === "full" ? 100 : lastPeriod ? lastPeriod.wl : 100;
      const partnerNameTrim = companyName.trim();
      // Engagement / company payload assembly. Partner: id wins, else name,
      // else nothing (server rejects with 422). Contractor: same priority but
      // empty values are fine — company is optional. Employee: never sends
      // company fields. Contractor explicit-unlink: send `companyId: null` so
      // the server clears the link (omitting the key would preserve it).
      const companyFields =
        engagement === "employee"
          ? {}
          : companyCleared && engagement === "contractor"
            ? { companyId: null }
            : companyId !== null
              ? { companyId }
              : partnerNameTrim
                ? { companyName: partnerNameTrim }
                : {};
      const partnerFields = { engagement, ...companyFields };
      const saved = await onSave({
        name: name.trim(),
        email: email.trim(),
        sysRole: sysRole === PLANNING_ONLY ? null : (sysRole as SysRole),
        emp,
        wl: currentWl,
        roles,
        periods: finalPeriods,
        status,
        excludeFromPlanning,
        ...partnerFields,
      });
      // If onSave returned undefined the save failed (saveError is now set
      // via the prop). Modal stays open and we DO NOT chain the cost PUT.
      if (!saved) return;

      // Cost-rate diff-save. Validate locally first; bail (modal stays open)
      // if any row is malformed. Then fire POST/PATCH/DELETE in parallel;
      // surface the first 409 inline on the offending row.
      if (canWriteCost) {
        setCostSaveError(null);
        setRowErrors(new Map());
        const localInvalid = new Map<number, "invalid">();

        // Partition into blocks; within each block, sort by effectiveFrom
        // (null first, then ASC). The "headline" of each block is the first
        // row — it may have effectiveFrom = "" (i.e. NULL). All other rows
        // MUST have a real start. validTo for each row is derived from the
        // next row's start (next.start − 1 month, or null if last).
        type SaveRow = EditRow & { computedValidTo: string | null };
        const blocks = new Map<number | "default", EditRow[]>();
        for (const r of editRows) {
          // Skip the seeded headline placeholder if the user never touched
          // it (no rate, no original id). The default block can remain
          // empty server-side.
          if (
            r.projectId === null &&
            r.originalId === null &&
            r.rate === "" &&
            r.effectiveFrom === ""
          ) {
            continue;
          }
          const key = r.projectId ?? ("default" as const);
          const list = blocks.get(key) ?? [];
          list.push(r);
          blocks.set(key, list);
        }
        const saveRows: SaveRow[] = [];
        for (const list of blocks.values()) {
          const sorted = [...list].sort((a, b) => {
            if (a.effectiveFrom === "" && b.effectiveFrom === "") return 0;
            if (a.effectiveFrom === "") return -1;
            if (b.effectiveFrom === "") return 1;
            return a.effectiveFrom.localeCompare(b.effectiveFrom);
          });
          for (let i = 0; i < sorted.length; i++) {
            const r = sorted[i];
            if (!r) continue;
            const isHeadline = i === 0 && r.effectiveFrom === "";
            const rateNum = Number.parseFloat(r.rate);
            if (r.rate === "" || !Number.isFinite(rateNum) || rateNum <= 0) {
              localInvalid.set(r.localId, "invalid");
              continue;
            }
            if (r.effectiveFrom !== "" && !YM_KEY_RE.test(r.effectiveFrom)) {
              localInvalid.set(r.localId, "invalid");
              continue;
            }
            if (!isHeadline && r.effectiveFrom === "") {
              // A non-headline row must have a start month.
              localInvalid.set(r.localId, "invalid");
              continue;
            }
            const nextStart = sorted[i + 1]?.effectiveFrom || "";
            const computedValidTo = nextStart !== "" ? prevYmKey(nextStart) : null;
            saveRows.push({ ...r, computedValidTo });
          }
        }
        if (localInvalid.size > 0) {
          setRowErrors(localInvalid);
          setCostSaveError("Some cost-rate rows are invalid — fix and re-save.");
          return;
        }
        const targetId = saved.id;
        // DELETEs FIRST — issue all deletes before any POST/PATCH so a swap
        // (remove old row, insert overlapping new one) doesn't trip the
        // route's app-layer overlap check mid-save.
        const deleteResults = await Promise.all(
          deletedIds.map((id) =>
            deleteCostRate(targetId, id)
              .then(() => ({ localId: null, result: "ok" as const }))
              .catch(() => ({ localId: null, result: "error" as const })),
          ),
        );
        // POSTs/PATCHes run SEQUENTIALLY within the same save. The server's
        // overlap check is a non-atomic SELECT-then-INSERT; firing parallel
        // requests against the same chain (e.g. user adds two overlapping
        // new rows on the Default block) can let both pass the SELECT, both
        // INSERT, and violate the invariant. Serialising here is the
        // simplest mitigation at our internal-tool scale.
        const mutateResults: Array<{
          localId: number | null;
          result: "ok" | "overlap" | "error";
        }> = [];
        for (const r of saveRows) {
          const rate = Number.parseFloat(r.rate);
          const eff = r.effectiveFrom === "" ? null : r.effectiveFrom;
          const vto = r.computedValidTo;
          const body = { projectId: r.projectId, effectiveFrom: eff, validTo: vto, rate };
          const handle = (err: { status?: number; body?: { code?: string } }) => {
            if (err.status === 409 || err.body?.code === "cost_rate_overlap") {
              return { localId: r.localId, result: "overlap" as const };
            }
            return { localId: r.localId, result: "error" as const };
          };
          if (r.originalId === null) {
            mutateResults.push(
              await createCostRate(targetId, body)
                .then(() => ({ localId: r.localId, result: "ok" as const }))
                .catch(handle),
            );
          } else if (r.originalSnapshot) {
            const o = r.originalSnapshot;
            const snapshotValidTo = o.validTo === "" ? null : o.validTo;
            if (
              o.projectId === r.projectId &&
              o.effectiveFrom === r.effectiveFrom &&
              snapshotValidTo === vto &&
              o.rate === rate
            ) {
              continue;
            }
            mutateResults.push(
              await patchCostRate(targetId, r.originalId, body)
                .then(() => ({ localId: r.localId, result: "ok" as const }))
                .catch(handle),
            );
          }
        }
        const results = [...deleteResults, ...mutateResults];
        const overlaps = results.filter((r) => r.result === "overlap");
        const others = results.filter((r) => r.result === "error");
        if (overlaps.length > 0 || others.length > 0) {
          const errs = new Map<number, "overlap" | "invalid">();
          for (const r of overlaps) {
            if (r.localId !== null) errs.set(r.localId, "overlap");
          }
          setRowErrors(errs);
          if (others.length > 0) {
            setCostSaveError(
              "Some cost-rate changes failed to save. Fix and re-save (already-saved rows are persisted).",
            );
          } else {
            setCostSaveError("One or more rows overlap an existing entry.");
          }
          // Refresh App-level state so the modal reflects what DID save.
          if (onSaveCost) {
            try {
              await onSaveCost(saved.id);
            } catch {
              /* surface via App-level error handler */
            }
          }
          return;
        }
        if (onSaveCost) {
          try {
            await onSaveCost(saved.id);
          } catch (err) {
            onCostSaveFailed?.(saved.id, err);
          }
        }
      }

      onClose();
    } finally {
      setBusy(false);
    }
  }

  const sysRoleOptions = [
    { v: PLANNING_ONLY, l: "Planning only — no login" },
    ...SYS_ROLES.map((r) => ({ v: r, l: r.charAt(0).toUpperCase() + r.slice(1) })),
  ];

  return (
    <Modal title={isEdit ? "Edit Person" : "Add Person"} onClose={onClose} wide>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
        }}
      >
        <Field label="Full Name">
          <Input value={name} onChange={setName} placeholder="Jane Doe" />
        </Field>
        <Field label="Email">
          <Input value={email} onChange={setEmail} placeholder="jane@dolmit.com" />
        </Field>
        <Field label="Status">
          <Sel
            value={status}
            onChange={(v) => setStatus(v as "active" | "archived")}
            options={[
              { v: "active", l: "Active" },
              { v: "archived", l: "Archived" },
            ]}
          />
        </Field>
        <Field label="Exclude from planning" style={{ gridColumn: "1 / -1" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 11px",
              borderRadius: 6,
              border: `1px solid ${C.line}`,
              background: C.surface,
              cursor: isAdmin || isLead ? "pointer" : "not-allowed",
              opacity: isAdmin || isLead ? 1 : 0.6,
              fontSize: 12.5,
              color: C.navy,
            }}
          >
            <input
              type="checkbox"
              checked={excludeFromPlanning}
              disabled={!(isAdmin || isLead)}
              onChange={(e) => setExcludeFromPlanning(e.target.checked)}
            />
            Hide from Dashboard, Bench, Reports, and project pickers
          </label>
        </Field>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
        }}
      >
        <Field label="System Role">
          {sysRoleEditable ? (
            <Sel value={sysRole} onChange={setSysRole} options={sysRoleOptions} />
          ) : (
            <div
              style={{
                padding: "7px 11px",
                borderRadius: 6,
                border: `1px solid ${C.line}`,
                fontSize: 13,
                color: C.slateMut,
                background: C.canvas,
              }}
            >
              {sysRole === PLANNING_ONLY
                ? "Planning only — no login"
                : sysRole.charAt(0).toUpperCase() + sysRole.slice(1)}
              <span style={{ fontSize: 10.5, marginLeft: 8, fontStyle: "italic" }}>
                (admin only)
              </span>
            </div>
          )}
        </Field>
        <Field label="Employment Type">
          <Sel
            value={emp}
            onChange={(v) => setEmp(v as "full" | "part")}
            options={[
              { v: "full", l: "Full-time" },
              { v: "part", l: "Part-time / Variable load" },
            ]}
          />
        </Field>
        {isAdmin && (
          <Field label="Engagement">
            <Sel
              value={engagement}
              onChange={(v) => {
                if (v !== "employee" && v !== "contractor" && v !== "partner") return;
                setEngagement(v);
                if (v === "employee") {
                  setCompanyId(null);
                  setCompanyName("");
                  setCompanyError(null);
                }
              }}
              options={[
                { v: "employee", l: "Employee" },
                { v: "contractor", l: "Contractor" },
                { v: "partner", l: "Partner" },
              ]}
            />
          </Field>
        )}
        {isAdmin && (engagement === "partner" || engagement === "contractor") && (
          <Field label={engagement === "partner" ? "Partner Company *" : "Company"}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Input
                value={companyName}
                onChange={(v) => {
                  setCompanyName(v);
                  if (companyError) setCompanyError(null);
                  if (companyId !== null) setCompanyId(null);
                  if (companyCleared) setCompanyCleared(false);
                }}
                placeholder={
                  companyId !== null
                    ? "(existing company linked)"
                    : engagement === "partner"
                      ? "Type company name…"
                      : "Type company name (optional)…"
                }
                invalid={!!companyError}
              />
              {engagement === "contractor" && companyId !== null && (
                <Btn
                  small
                  onClick={() => {
                    setCompanyId(null);
                    setCompanyName("");
                    setCompanyCleared(true);
                    setCompanyError(null);
                  }}
                >
                  Unlink
                </Btn>
              )}
            </div>
            {companyError && (
              <div style={{ color: C.r, fontSize: 11, marginTop: 4 }}>{companyError}</div>
            )}
          </Field>
        )}
      </div>

      <Field label="Job Roles">
        <TagEditor
          value={roles}
          onChange={setRoles}
          suggestions={suggestions}
          placeholder="+ add a role…"
        />
        <div
          style={{
            fontSize: 10.5,
            color: C.slateMut,
            marginTop: 4,
            fontStyle: "italic",
          }}
        >
          Free-form tags — used to match people to project slots.
        </div>
      </Field>

      {canReadCost && (
        <div style={{ marginTop: 6 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.slateMut,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Cost rates
              <InfoIcon
                ariaLabel="About cost rates"
                tooltip="Each row sets the rate from its month forward. Add a new row to step the rate up or down — the previous row's end is derived automatically. The default block covers every project unless a project-specific row overrides it for that month; project gaps fall back to the default. Deleting a row affects historical reports retroactively — totals already reported may change."
              />
            </span>
          </div>
          {grouped.map((g) => (
            <div
              key={g.projectId === null ? "__default" : g.projectId}
              style={{ marginBottom: 10 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: g.archived ? C.slateMut : C.navy,
                  fontStyle: g.archived ? "italic" : "normal",
                  marginBottom: 4,
                }}
              >
                <span>
                  {g.projectName}
                  {g.archived && (
                    <span style={{ fontWeight: 400, marginLeft: 6, color: C.slateMut }}>
                      (archived)
                    </span>
                  )}
                </span>
                {canWriteCost && g.projectId !== null && (
                  <button
                    type="button"
                    onClick={() => removeProjectGroup(g.projectId as number)}
                    title="Remove this project's rate block"
                    aria-label={`Remove rate block for ${g.projectName}`}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: C.r,
                      fontSize: 14,
                      padding: "2px 6px",
                      borderRadius: 4,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              {g.rows.map((row, idx) => {
                const err = rowErrors.get(row.localId);
                const rateNum = Number.parseFloat(row.rate);
                const rateInvalid = row.rate === "" || !Number.isFinite(rateNum) || rateNum <= 0;
                // Period UI: one DatePicker per row (sets effectiveFrom).
                // The button face shows the derived range using the next row
                // in the block to compute the end (no explicit end input).
                const isHeadline = idx === 0;
                const label = periodLabel(row, g.rows);
                return (
                  <div
                    key={row.localId}
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 6,
                    }}
                  >
                    <div style={DATE_CELL_STYLE}>
                      <DatePicker
                        value={row.effectiveFrom}
                        onChange={(v) =>
                          canWriteCost && setRowField(row.localId, { effectiveFrom: v })
                        }
                        monthOnly
                        allowClear={isHeadline}
                        placeholder={isHeadline ? "Headline (no date)" : "Pick a start month"}
                        {...(label !== null ? { displayOverride: label } : {})}
                      />
                    </div>
                    <div style={{ flex: "0 1 110px", minWidth: 90 }}>
                      <Input
                        type="text"
                        value={row.rate}
                        onChange={(v) =>
                          canWriteCost && setRowField(row.localId, { rate: v.replace(",", ".") })
                        }
                        readOnly={!canWriteCost}
                        suffix="€/h"
                        invalid={(rateInvalid && row.rate !== "") || err === "invalid"}
                        aria-label="Rate"
                      />
                    </div>
                    {canWriteCost && !(g.projectId === null && g.rows.length === 1) && (
                      <button
                        type="button"
                        onClick={() => removeRow(row.localId)}
                        title="Remove cost rate"
                        aria-label="Remove cost rate"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: C.r,
                          fontSize: 14,
                          padding: "4px 6px",
                          borderRadius: 4,
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        ×
                      </button>
                    )}
                    {err && (
                      <div
                        style={{
                          flexBasis: "100%",
                          color: C.r,
                          fontSize: 11,
                          marginTop: 2,
                        }}
                      >
                        {err === "overlap"
                          ? "This period overlaps with another rate in this block"
                          : rateInvalid
                            ? "Rate must be greater than 0"
                            : !isHeadline && row.effectiveFrom === ""
                              ? "Pick a start month for this rate"
                              : "Period bounds must be YYYY-MM"}
                      </div>
                    )}
                  </div>
                );
              })}
              {canWriteCost && (
                <Btn small onClick={() => addRow(g.projectId)}>
                  + Add rate
                </Btn>
              )}
            </div>
          ))}
          {canWriteCost && (
            <div style={{ marginTop: 8 }}>
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") return;
                  addRow(Number(v));
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${C.line}`,
                  fontSize: 12.5,
                  background: C.surface,
                }}
                aria-label="Add a project rate group"
              >
                <option value="">+ Add project rate…</option>
                {sortByName(nonArchivedProjects.filter((p) => !usedProjectIds.has(p.id))).map(
                  (p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ),
                )}
                {showArchived &&
                  sortByName(archivedProjects.filter((p) => !usedProjectIds.has(p.id))).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (archived)
                    </option>
                  ))}
              </select>
              {archivedProjects.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowArchived((v) => !v)}
                  style={{
                    background: "none",
                    border: "none",
                    color: C.slateMut,
                    fontSize: 11,
                    cursor: "pointer",
                    marginLeft: 8,
                    textDecoration: "underline",
                  }}
                >
                  {showArchived ? "Hide archived" : "Show archived"}
                </button>
              )}
            </div>
          )}
          {(() => {
            // Live preview — one line per block, listing every period in
            // chronological order with the row's contextual label. Skips
            // rows with an unparseable rate (draft state).
            const fmtRate = (r: string): string => {
              const n = Number.parseFloat(r);
              return Number.isFinite(n) && n > 0 ? `${n} €/h` : "—";
            };
            // Each row contributes "rate" (headline) or "rate from <month>"
            // (dated), plus a diff marker against the loaded snapshot:
            //   (new)         — row didn't exist when the modal opened
            //   (was N €/h)   — rate changed
            // Removed rows aren't surfaced (they're not in editRows).
            const partForRow = (r: EditRow): string => {
              const base =
                r.effectiveFrom === ""
                  ? fmtRate(r.rate)
                  : `${fmtRate(r.rate)} from ${formatYm(r.effectiveFrom)}`;
              if (r.originalId === null) return `${base} (new)`;
              const o = r.originalSnapshot;
              if (o) {
                const newRate = Number.parseFloat(r.rate);
                if (Number.isFinite(newRate) && newRate !== o.rate) {
                  return `${base} (was ${o.rate} €/h)`;
                }
              }
              return base;
            };
            const renderBlock = (name: string, rows: ReadonlyArray<EditRow>): string | null => {
              const usable = rows.filter((r) => {
                const n = Number.parseFloat(r.rate);
                return r.rate !== "" && Number.isFinite(n) && n > 0;
              });
              const only = usable[0];
              if (!only) return null;
              if (usable.length === 1) {
                return `${name} → ${partForRow(only)}`;
              }
              return `${name}: ${usable.map(partForRow).join(", then ")}`;
            };
            const lines: string[] = [];
            for (const g of grouped) {
              if (g.projectId === null) continue;
              const line = renderBlock(g.projectName, g.rows);
              if (line) lines.push(line);
            }
            const defaultRows = grouped.find((g) => g.projectId === null)?.rows ?? [];
            const defaultLine = renderBlock(
              grouped.some((g) => g.projectId !== null) ? "Other projects" : "All projects",
              defaultRows,
            );
            if (defaultLine) lines.push(defaultLine);
            if (lines.length === 0) return null;
            return (
              <div
                aria-label="Cost rate preview"
                style={{
                  marginTop: 10,
                  padding: "8px 12px",
                  background: C.canvas,
                  border: `1px solid ${C.line}`,
                  borderRadius: 6,
                  fontSize: 11.5,
                  color: C.navy,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <InfoIcon
                  ariaLabel="Preview info"
                  variant="compact"
                  tooltip="Live preview of the rates this person will have on save. Each block lists its periods in chronological order; updates as you edit."
                />
                <span>
                  <strong>Preview:</strong> {lines.join(". ")}
                </span>
              </div>
            );
          })()}
          {costSaveError && (
            <div style={{ color: C.r, fontSize: 11.5, marginTop: 6 }}>{costSaveError}</div>
          )}
        </div>
      )}

      {emp === "part" && (
        <div style={{ marginTop: 2 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.slateMut,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              Workload Periods
            </span>
            <Btn small onClick={addPeriod}>
              + Add Period
            </Btn>
          </div>

          {periods.map((p, idx) => {
            const periodLocked = isPast(p.to) && !isAdmin && !isLead;
            const overlap = periods.some((other, oi) => {
              if (oi === idx || !p.from || !p.to || !other.from || !other.to) return false;
              const pF = new Date(p.from).getTime();
              const pT = new Date(p.to).getTime();
              const oF = new Date(other.from).getTime();
              const oT = new Date(other.to).getTime();
              return pF <= oT && pT >= oF;
            });
            return (
              <div
                key={`${p.from}-${idx}`}
                style={{
                  background: periodLocked ? C.canvas : overlap ? C.rBg : C.surface,
                  border: `1px solid ${overlap ? C.rBd : periodLocked ? C.lineSoft : C.line}`,
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 8,
                  opacity: periodLocked ? 0.75 : 1,
                  position: "relative",
                }}
              >
                {periodLocked && (
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 10,
                      fontSize: 10,
                      color: C.slateMut,
                    }}
                  >
                    🔒 Past period (admin only)
                  </div>
                )}
                {overlap && !periodLocked && (
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 10,
                      fontSize: 10,
                      color: C.r,
                      fontWeight: 600,
                    }}
                  >
                    ⚠ Overlaps
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 90px 28px",
                    gap: 10,
                    alignItems: "end",
                  }}
                >
                  <Field label="From" style={{ marginBottom: 0 }}>
                    <DatePicker
                      dropUp
                      dropLeft
                      value={p.from || ""}
                      onChange={(v) => {
                        if (!periodLocked) updatePeriod(idx, "from", v);
                      }}
                    />
                  </Field>
                  <Field label="To" style={{ marginBottom: 0 }}>
                    <DatePicker
                      dropUp
                      dropLeft
                      value={p.to || ""}
                      onChange={(v) => {
                        if (!periodLocked) updatePeriod(idx, "to", v);
                      }}
                    />
                  </Field>
                  <Field label="Load" style={{ marginBottom: 0 }}>
                    <Input
                      type="number"
                      value={String(p.wl || "")}
                      onChange={(v) => !periodLocked && updatePeriod(idx, "wl", Number(v))}
                      suffix="%"
                      disabled={periodLocked}
                    />
                  </Field>
                  <div style={{ paddingBottom: 2 }}>
                    {!periodLocked && (
                      <button
                        type="button"
                        onClick={() => removePeriod(idx)}
                        title="Remove"
                        aria-label="Remove period"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: C.r,
                          fontSize: 14,
                          padding: "4px 6px",
                          borderRadius: 4,
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasOverlap && (
        <div
          role="alert"
          style={{
            background: C.rBg,
            border: `1px solid ${C.rBd}`,
            borderRadius: 6,
            padding: "8px 12px",
            marginTop: 10,
            fontSize: 12,
            color: C.r,
            fontWeight: 600,
          }}
        >
          ⚠ Periods overlap — please fix before saving
        </div>
      )}

      {saveError && (
        <div
          role="alert"
          style={{
            background: C.rBg,
            border: `1px solid ${C.rBd}`,
            borderRadius: 6,
            padding: "8px 12px",
            marginTop: 10,
            fontSize: 12,
            color: C.r,
          }}
        >
          {saveError}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 18,
          paddingTop: 14,
          borderTop: `1px solid ${C.lineSoft}`,
        }}
      >
        <div>
          {isEdit && onDelete && person.canHardDelete === true && isAdmin && (
            <DeleteButton onDelete={onDelete} />
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn primary disabled={hasOverlap || busy} onClick={submit}>
            {busy ? (isEdit ? "Saving…" : "Adding…") : isEdit ? "Save Changes" : "Add Person"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

export interface ProjectModalProps {
  project: Project | null;
  people: Person[];
  onClose: () => void;
  onSave: (input: ProjectInput) => void;
  /** Admin/Lead-only delete handler; absent in create mode. */
  onDelete?: () => void | Promise<void>;
  /** Caller's effective role; gates the destructive affordance. */
  currentUserRole?: string;
  /** Surface async-save / delete errors; same shape as PersonModal. */
  saveError?: string | null;
}

interface RateHistoryDraft {
  effectiveFrom: string;
  fr: number | null;
  roles: Record<string, number>;
}

export function ProjectModal({
  project,
  people,
  onClose,
  onSave,
  onDelete,
  currentUserRole,
  saveError,
}: ProjectModalProps) {
  const { C } = useC();
  const isEdit = !!project;
  const [name, setName] = useState(project?.name ?? "");
  const [code, setCode] = useState(project?.code ?? "");
  const [status, setStatus] = useState<ProjectStatus>(project?.status ?? "planned");
  const [isTimeOff, setIsTimeOff] = useState(!!project?.isTimeOff);
  const [pmId, setPmId] = useState<number | null>(
    project?.pmId ?? people.find((p) => p.sysRole === "pm")?.id ?? people[0]?.id ?? null,
  );
  const [leadId, setLeadId] = useState<number | null>(
    project?.leadId ?? people.find((p) => p.sysRole === "lead")?.id ?? null,
  );
  const [pm, setPm] = useState<PricingMode>(project?.pm ?? "by_role");
  const [rateHistory, setRateHistory] = useState<RateHistoryDraft[]>(() => {
    if (Array.isArray(project?.rateHistory) && project.rateHistory.length) {
      return project.rateHistory.map((e) => ({
        effectiveFrom: e.effectiveFrom || "",
        fr: e.fr ?? null,
        roles: e.roles ? { ...e.roles } : {},
      }));
    }
    const seed: RateHistoryDraft = {
      effectiveFrom: "",
      fr: project?.fr ?? 90,
      roles: project?.roles ? { ...project.roles } : { "Project Manager": 100 },
    };
    return [seed];
  });
  function sortRateHistory(arr: RateHistoryDraft[]) {
    return [...arr].sort((a, b) => {
      const af = a.effectiveFrom || "";
      const bf = b.effectiveFrom || "";
      return af.localeCompare(bf);
    });
  }
  function updateRateEntry(idx: number, patch: Partial<RateHistoryDraft>) {
    setRateHistory((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  }
  function addRateEntry() {
    const last = rateHistory[rateHistory.length - 1];
    const next: RateHistoryDraft = {
      effectiveFrom: "",
      fr: last?.fr ?? 90,
      roles: last?.roles ? { ...last.roles } : {},
    };
    setRateHistory([...rateHistory, next]);
  }
  function removeRateEntry(idx: number) {
    if (rateHistory.length <= 1) return;
    setRateHistory(rateHistory.filter((_, i) => i !== idx));
  }
  const activeRoleNames = useMemo(() => {
    const s = new Set<string>();
    for (const e of rateHistory) for (const r of Object.keys(e.roles)) s.add(r);
    return [...s];
  }, [rateHistory]);
  function setRoleRateInEntry(idx: number, rname: string, val: number) {
    updateRateEntry(idx, { roles: { ...(rateHistory[idx]?.roles ?? {}), [rname]: val } });
  }
  function addRoleEverywhere(rname: string) {
    if (!rname || activeRoleNames.includes(rname)) return;
    setRateHistory(rateHistory.map((e) => ({ ...e, roles: { ...e.roles, [rname]: 90 } })));
  }
  function removeRoleEverywhere(rname: string) {
    setRateHistory(
      rateHistory.map((e) => {
        const n = { ...e.roles };
        delete n[rname];
        return { ...e, roles: n };
      }),
    );
  }
  const [budget, setBudget] = useState(project?.budget ?? 0);
  const [mt, setMt] = useState(project?.mt ?? 25);
  const [tol, setTol] = useState(project?.tol ?? 5);
  const [hm, setHm] = useState(String(project?.hm ?? 1));

  const pmOptions = people
    .filter((p) => p.sysRole === "pm" || p.sysRole === "admin" || p.sysRole === "lead")
    .map((p) => ({ v: String(p.id), l: p.name }));
  const leadOptions = [{ v: "", l: "— none —" }].concat(
    people
      .filter((p) => p.sysRole === "lead" || p.sysRole === "admin")
      .map((p) => ({ v: String(p.id), l: p.name })),
  );

  const roleNames = activeRoleNames;
  const catalogue = JOB_ROLES_SEED as readonly string[];

  const nameValid = name.trim().length > 0;
  const codeValid = code.trim().length > 0;
  const hmNum = Number(hm);
  const hmValid = hm !== "" && !Number.isNaN(hmNum) && hmNum > 0;
  const rateHistValid = rateHistory.every((e) =>
    pm === "flat" ? Number(e.fr) > 0 : Object.keys(e.roles).length > 0,
  );
  const rateHistUnique = (() => {
    const seen = new Set<string>();
    for (const e of rateHistory) {
      const k = e.effectiveFrom || "__start__";
      if (seen.has(k)) return false;
      seen.add(k);
    }
    return true;
  })();
  const canSave =
    nameValid && codeValid && hmValid && (isTimeOff || (rateHistValid && rateHistUnique));

  return (
    <Modal title={isEdit ? `Edit Project — ${project.name}` : "New Project"} onClose={onClose} wide>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <Field label="Project Name">
          <Input value={name} onChange={setName} placeholder="E.g. HTV Road Infrastructure" />
        </Field>
        <Field
          label={
            <>
              Code
              <InfoIcon
                tooltip={
                  <>
                    Semicolon-separated tokens. A Jira <strong>project key</strong> (e.g.{" "}
                    <code>DIT</code>) pulls every issue under that project including subtasks. A
                    Jira <strong>issue key</strong> (e.g. <code>DIT-19</code>) pulls that issue plus
                    its direct subtasks. Anything else is an opaque code that does not sync from
                    Jira. The same Jira issue may appear in multiple planner projects — hours are
                    counted into each (fan-out), which can double-count if summed across overlapping
                    projects.
                  </>
                }
              />
            </>
          }
        >
          <Input value={code} onChange={setCode} placeholder="MSA2 or DIT-19;DIT-20;MSA" />
        </Field>
        <Field label="Status">
          <Sel
            value={status}
            onChange={(v) => setStatus(v as ProjectStatus)}
            options={[
              { v: "planned", l: "Planned" },
              { v: "active", l: "Active" },
              { v: "archived", l: "Archived" },
            ]}
          />
        </Field>
        <Field label="Time off / non-billable">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              height: 32,
              fontSize: 13,
              color: C.slate,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={isTimeOff}
              onChange={(e) => setIsTimeOff(e.target.checked)}
            />
            <span>Treat as time-off project</span>
          </label>
        </Field>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
        }}
      >
        {!isTimeOff && (
          <Field label="Project Manager">
            <Sel
              value={String(pmId ?? "")}
              onChange={(v) => setPmId(Number(v))}
              options={pmOptions}
            />
          </Field>
        )}
        {!isTimeOff && (
          <Field label="Team Lead">
            <Sel
              value={leadId == null ? "" : String(leadId)}
              onChange={(v) => setLeadId(v === "" ? null : Number(v))}
              options={leadOptions}
            />
          </Field>
        )}
        {!isTimeOff && (
          <Field label="Billing Model">
            <Sel
              value={pm}
              onChange={(v) => setPm(v as PricingMode)}
              options={[
                { v: "by_role", l: "Per-role rates" },
                { v: "flat", l: "Flat rate (one price for all)" },
              ]}
            />
          </Field>
        )}
        {isTimeOff && (
          <div
            style={{
              fontSize: 12,
              color: C.slateMut,
              fontStyle: "italic",
              padding: "8px 2px",
              gridColumn: "1 / -1",
            }}
          >
            Time-off projects have no PM, billing or budget — they only model capacity consumed by
            vacation, sick leave, public holidays, etc.
          </div>
        )}
      </div>

      {!isTimeOff && (
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 6,
            }}
          >
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: C.slateMut,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              {pm === "flat" ? "Flat Rate History" : "Per-role Rate Card History"}
              {rateHistory.length > 1 && (
                <span
                  style={{
                    marginLeft: 8,
                    color: C.blue,
                    textTransform: "none",
                    letterSpacing: 0,
                    fontWeight: 600,
                    fontSize: 10.5,
                  }}
                >
                  · {rateHistory.length} entries
                </span>
              )}
            </label>
            <Btn
              small
              onClick={addRateEntry}
              icon={
                <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
                  <path
                    d="M6 2v8M2 6h8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              }
            >
              Add rate change
            </Btn>
          </div>

          {pm === "by_role" && (
            <div
              style={{
                background: C.surfaceAlt,
                border: `1px solid ${C.line}`,
                borderRadius: 8,
                padding: 10,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  color: C.slateMut,
                  marginBottom: 6,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                Roles on this project
              </div>
              {roleNames.length === 0 && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: C.slateMut,
                    fontStyle: "italic",
                    padding: "4px 2px",
                  }}
                >
                  No roles yet — add one below.
                </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {roleNames.map((rname) => (
                  <span
                    key={rname}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <Badge tone={roleTone(rname) as never}>{rname}</Badge>
                    <button
                      type="button"
                      onClick={() => removeRoleEverywhere(rname)}
                      title="Remove role"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: C.r,
                        fontSize: 12,
                        padding: "0 4px",
                        borderRadius: 4,
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <AddRoleRow
                onAdd={addRoleEverywhere}
                declared={roleNames}
                catalogue={catalogue as string[]}
              />
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rateHistory.map((entry, idx) => {
              const dup = rateHistory.some(
                (o, i) => i !== idx && (o.effectiveFrom || "") === (entry.effectiveFrom || ""),
              );
              const entryValid =
                pm === "flat" ? Number(entry.fr) > 0 : Object.keys(entry.roles).length > 0;
              const sorted = sortRateHistory(rateHistory);
              const myPos = sorted.findIndex((e) => e === entry);
              const nextStart =
                myPos >= 0 && myPos < sorted.length - 1 ? sorted[myPos + 1]?.effectiveFrom : null;
              return (
                <div
                  key={`${idx}-${entry.effectiveFrom || "start"}`}
                  style={{
                    border: `1px solid ${dup ? C.r : C.line}`,
                    borderRadius: 8,
                    padding: 12,
                    background: C.surface,
                    position: "relative",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => removeRateEntry(idx)}
                    disabled={rateHistory.length <= 1}
                    title={
                      rateHistory.length <= 1
                        ? "At least one rate entry required"
                        : "Remove this rate change"
                    }
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: "none",
                      border: "none",
                      cursor: rateHistory.length <= 1 ? "not-allowed" : "pointer",
                      color: rateHistory.length <= 1 ? C.slateMut : C.r,
                      fontSize: 16,
                      padding: "4px 6px",
                      borderRadius: 4,
                      lineHeight: 1,
                      opacity: rateHistory.length <= 1 ? 0.4 : 1,
                    }}
                  >
                    ×
                  </button>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "240px 1fr",
                      gap: 14,
                      alignItems: "flex-start",
                      paddingRight: 24,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: 11.5,
                          color: C.slate,
                          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        Effective period
                      </label>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
                      >
                        <DatePicker
                          monthOnly
                          allowClear
                          dropLeft
                          value={entry.effectiveFrom || ""}
                          placeholder="From the beginning"
                          onChange={(v) => updateRateEntry(idx, { effectiveFrom: v || "" })}
                        />
                        <span style={{ fontSize: 11, color: C.slateMut }}>→</span>
                        <span
                          style={{
                            fontSize: 12,
                            color: C.slateMut,
                            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                            fontVariantNumeric: "tabular-nums",
                            padding: "4px 8px",
                            background: C.surfaceAlt,
                            borderRadius: 5,
                            border: `1px dashed ${C.line}`,
                          }}
                        >
                          {nextStart
                            ? (() => {
                                const [yy, mm] = nextStart.split("-").map(Number);
                                const endDate = new Date(yy ?? 0, (mm ?? 1) - 2, 1);
                                const monthShort = [
                                  "Jan",
                                  "Feb",
                                  "Mar",
                                  "Apr",
                                  "May",
                                  "Jun",
                                  "Jul",
                                  "Aug",
                                  "Sep",
                                  "Oct",
                                  "Nov",
                                  "Dec",
                                ];
                                return `until ${monthShort[endDate.getMonth()]} ${endDate.getFullYear()}`;
                              })()
                            : "open-ended"}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: dup ? C.r : C.slateMut,
                          marginTop: 4,
                          fontStyle: "italic",
                        }}
                      >
                        {dup
                          ? "Duplicate — each entry needs a unique month"
                          : entry.effectiveFrom
                            ? `Applies from ${entry.effectiveFrom}`
                            : "Applies from the beginning"}
                        {!dup && nextStart && ` · ends when next rate begins (${nextStart})`}
                      </div>
                    </div>
                    <div>
                      {pm === "flat" ? (
                        <Field label="Flat rate" style={{ marginBottom: 0 }}>
                          <div style={{ maxWidth: 180 }}>
                            <Input
                              type="number"
                              value={String(entry.fr ?? 0)}
                              onChange={(v) => updateRateEntry(idx, { fr: Number(v) || 0 })}
                              suffix="€/h"
                            />
                          </div>
                        </Field>
                      ) : (
                        <div>
                          <label
                            style={{
                              fontSize: 11.5,
                              color: C.slate,
                              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                              display: "block",
                              marginBottom: 6,
                            }}
                          >
                            Rates
                          </label>
                          {roleNames.length === 0 ? (
                            <div style={{ fontSize: 11, color: C.slateMut, fontStyle: "italic" }}>
                              Add roles above to set rates.
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                                gap: 8,
                                rowGap: 8,
                              }}
                            >
                              {roleNames.map((rname) => (
                                <div
                                  key={rname}
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(0, 1fr) 110px",
                                    gap: 8,
                                    alignItems: "center",
                                    minWidth: 0,
                                  }}
                                >
                                  <div style={{ minWidth: 0, overflow: "hidden" }}>
                                    <Badge tone={roleTone(rname) as never}>{rname}</Badge>
                                  </div>
                                  <Input
                                    type="number"
                                    value={String(entry.roles[rname] ?? 0)}
                                    onChange={(v) => setRoleRateInEntry(idx, rname, Number(v) || 0)}
                                    suffix="€/h"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {!entryValid && (
                    <div
                      style={{
                        fontSize: 10.5,
                        color: C.r,
                        marginTop: 6,
                        fontStyle: "italic",
                      }}
                    >
                      {pm === "flat"
                        ? "Flat rate must be greater than 0."
                        : "Needs at least one role rate."}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 140px",
              gap: 10,
              alignItems: "center",
              marginTop: 12,
              paddingTop: 10,
              borderTop: `1px dashed ${C.lineSoft}`,
            }}
          >
            <label
              style={{
                fontSize: 11.5,
                color: C.slate,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              Hours Multiplier{" "}
              <span style={{ color: C.slateMut, fontWeight: 400 }}>— budget only</span>
            </label>
            <Input type="number" value={hm} onChange={setHm} suffix="×" />
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: hmValid ? C.slateMut : C.r,
              marginTop: 4,
              fontStyle: "italic",
            }}
          >
            {hmValid
              ? "Scales planned hours when calculating revenue (e.g. 1.2× if you sold more hours than you plan to spend). Does not affect cost."
              : "Hours multiplier is required (must be greater than 0). Use 1 for no scaling."}
          </div>
        </div>
      )}

      {!isTimeOff && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
          }}
        >
          <Field label="Budget">
            <Input
              type="number"
              value={String(budget)}
              onChange={(v) => setBudget(Number(v) || 0)}
              suffix="€"
            />
          </Field>
          <Field label="Margin Target">
            <Input
              type="number"
              value={String(mt)}
              onChange={(v) => setMt(Number(v) || 0)}
              suffix="%"
            />
          </Field>
          <Field label="Tolerance">
            <Input
              type="number"
              value={String(tol)}
              onChange={(v) => setTol(Number(v) || 0)}
              suffix="±%"
            />
          </Field>
        </div>
      )}

      {saveError && (
        <div
          style={{
            color: C.r,
            background: C.rBg,
            border: `1px solid ${C.rBd}`,
            fontSize: 13,
            marginTop: 12,
            padding: 8,
            borderRadius: 6,
          }}
        >
          {saveError}
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 18,
          paddingTop: 14,
          borderTop: `1px solid ${C.lineSoft}`,
        }}
      >
        <div>
          {isEdit &&
            onDelete &&
            project.canHardDelete === true &&
            (currentUserRole === "admin" || currentUserRole === "lead") && (
              <DeleteButton onDelete={onDelete} />
            )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn
            primary
            disabled={!canSave}
            onClick={() => {
              const sortedHist: RateHistoryEntry[] = sortRateHistory(rateHistory).map((e) => ({
                effectiveFrom: e.effectiveFrom || "",
                ...(pm === "flat" ? { fr: Number(e.fr) || 0 } : { roles: { ...e.roles } }),
              }));
              const first = sortedHist[0];
              const input: ProjectInput = {
                name: name.trim(),
                code: code.trim(),
                status,
                isTimeOff,
                pmId,
                leadId,
                pm,
                rateHistory: sortedHist,
                fr: pm === "flat" ? (first?.fr ?? 0) : null,
                roles: pm === "by_role" ? (first?.roles ?? {}) : {},
                budget: Number(budget) || 0,
                mt: Number(mt) || 0,
                tol: Number(tol) || 0,
                hm: hmNum,
              };
              onSave(input);
            }}
          >
            {isEdit ? "Save Changes" : "Create Project"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

export function DeleteButton({ onDelete }: { onDelete: () => void | Promise<void> }) {
  const [pending, setPending] = useState(false);
  return (
    <Btn
      disabled={pending}
      onClick={async () => {
        setPending(true);
        try {
          await onDelete();
        } finally {
          setPending(false);
        }
      }}
    >
      {pending ? "Deleting…" : "Delete"}
    </Btn>
  );
}

function AddRoleRow({
  onAdd,
  declared,
  catalogue,
}: {
  onAdd: (rname: string) => void;
  declared: string[];
  catalogue: string[];
}) {
  const { C } = useC();
  const [adding, setAdding] = useState(false);
  const [tmp, setTmp] = useState("");
  if (!adding)
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          background: "transparent",
          border: `1px dashed ${C.line}`,
          padding: "4px 10px",
          borderRadius: 6,
          cursor: "pointer",
          color: C.slateMut,
          fontSize: 11.5,
          marginTop: 4,
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = C.blue;
          e.currentTarget.style.color = C.blueDeep;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = C.line;
          e.currentTarget.style.color = C.slateMut;
        }}
      >
        + Add role
      </button>
    );
  return (
    <div style={{ marginTop: 6 }}>
      <RolePicker
        value={tmp}
        onChange={(v) => {
          onAdd(v);
          setTmp("");
          setAdding(false);
        }}
        declared={[]}
        catalogue={catalogue.filter((c) => !declared.includes(c))}
      />
    </div>
  );
}
